<span [pTooltip]="disabledReason() ?? ''" tooltipPosition="left">
    <p-button
        [label]="'Generate with AI'"
        [icon]="aiIcon"
        severity="secondary"
        [text]="true"
        size="small"
        [disabled]="disabled()"
        (click)="open()"
    />
</span>

<p-dialog
    header="AI — brand logo"
    [visible]="visible()"
    (visibleChange)="onVisibleChange($event)"
    [style]="{ width: '52rem' }"
    [breakpoints]="{ '1024px': '95vw' }"
    [modal]="true"
    [closable]="phase() !== 'loading'"
    [draggable]="false"
>
    <div class="flex flex-col gap-4">
        @if (error(); as e) {
        <p-message
            severity="error"
            icon="pi pi-times-circle"
            styleClass="w-full"
        >
            <span class="text-sm">{{ e }}</span>
        </p-message>
        }

        <!-- form -->
        @if (phase() === 'form') {
        <div class="flex flex-col gap-4">
            <small class="text-muted-color">
                Generates an isolated brand mark (transparent) for
                <b>{{ brandName() || 'this realm' }}</b>. The favicon is derived
                from the chosen mark automatically. @if (style()) { Uses the
                template's style descriptor. } @else {
                <span class="text-amber-600"
                    >No template style available — the mark will be generic.</span
                >
                }
            </small>

            <div>
                <div class="block font-medium mb-1 text-sm">
                    Brand references
                    <span class="font-normal text-muted-color">(from the template, {{ references().length }})</span>
                </div>
                @if (references().length > 0) {
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    @for (ref of references(); track ref.dataUrl) {
                    <div
                        class="aspect-square rounded border border-surface-200 dark:border-surface-700 bg-surface-100 dark:bg-surface-800 overflow-hidden flex items-center justify-center p-1"
                    >
                        <img
                            [src]="ref.dataUrl"
                            alt="Brand reference"
                            class="max-w-full max-h-full object-contain"
                        />
                    </div>
                    }
                </div>
                } @else {
                <small class="text-amber-600">This template ships no brand references.</small>
                }
                <small class="block text-muted-color mt-1">
                    Text‑free anchors from the assigned Site Template — used as the
                    style/brand reference for the generated mark.
                </small>
            </div>

            <div>
                <label class="block font-medium mb-1 text-sm">Variants</label>
                <p-selectbutton
                    [options]="sampleOptions"
                    optionLabel="label"
                    optionValue="value"
                    [ngModel]="samples()"
                    (ngModelChange)="samples.set($event ?? 4)"
                    [allowEmpty]="false"
                />
            </div>

            <div class="flex flex-col gap-2">
                <div class="flex items-center gap-2">
                    <p-checkbox
                        inputId="logo-manual-prompt"
                        [binary]="true"
                        [ngModel]="useManualPrompt()"
                        (ngModelChange)="useManualPrompt.set($event)"
                    />
                    <label for="logo-manual-prompt" class="text-sm"
                        >Write my own prompt (otherwise AI authors it from the
                        style)</label
                    >
                </div>
                @if (useManualPrompt()) {
                <textarea
                    pTextarea
                    class="w-full"
                    rows="2"
                    placeholder="example: a glossy 3D abstract gem mark, neon edges"
                    [ngModel]="prompt()"
                    (ngModelChange)="prompt.set($event ?? '')"
                ></textarea>
                }
            </div>

            @if (creditBlock(); as block) {
            <p-message severity="warn" icon="pi pi-wallet" styleClass="w-full">
                <span class="text-sm">{{ block }}</span>
            </p-message>
            }

            <div class="flex justify-end">
                <p-button
                    label="Generate"
                    [icon]="aiIcon"
                    [disabled]="!canGenerate()"
                    (click)="generate()"
                />
            </div>
        </div>
        }

        <!-- loading -->
        @if (phase() === 'loading') {
        <div class="flex flex-col items-center justify-center gap-3 py-10">
            <p-progress-spinner
                styleClass="w-12 h-12"
                strokeWidth="4"
                ariaLabel="Generating"
            />
            <div class="text-sm text-muted-color">
                Generating {{ samples() }} mark(s) — this can take up to a
                minute…
            </div>
        </div>
        }

        <!-- results -->
        @if (phase() === 'results') {
        <div class="flex flex-col gap-3">
            <div class="flex items-center justify-between">
                <div class="text-sm text-muted-color">
                    Pick a mark to apply (sets both the logo icon and the
                    favicon).
                </div>
                <p-button
                    label="Back"
                    icon="pi pi-arrow-left"
                    severity="secondary"
                    outlined
                    size="small"
                    (click)="backToForm()"
                />
            </div>

            @if (candidates().length === 0) {
            <p-message severity="warn" styleClass="w-full">
                <span class="text-sm">No candidates were returned.</span>
            </p-message>
            } @else {
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                @for (c of candidates(); track c.url) {
                <div
                    class="rounded border border-surface-200 dark:border-surface-700 overflow-hidden flex flex-col"
                >
                    <div
                        class="aspect-square flex items-center justify-center bg-surface-100 dark:bg-surface-800 p-2"
                    >
                        <img
                            [src]="c.url"
                            alt="Brand mark candidate"
                            class="max-w-full max-h-full object-contain"
                            referrerpolicy="no-referrer"
                        />
                    </div>
                    <div
                        class="flex items-center justify-between gap-2 px-2 py-1.5 border-t border-surface-200 dark:border-surface-700"
                    >
                        @if (c.favicon?.url; as fav) {
                        <img
                            [src]="fav"
                            alt="favicon"
                            title="Favicon variant"
                            class="w-5 h-5 object-contain flex-shrink-0"
                            referrerpolicy="no-referrer"
                        />
                        }
                        <p-button
                            label="Use"
                            icon="pi pi-check"
                            size="small"
                            (click)="use(c)"
                        />
                    </div>
                </div>
                }
            </div>
            }
        </div>
        }
    </div>
</p-dialog>
